import { DirectorUser } from '@kaizenplatform/hugot-entity';
import { createElement, FC, useContext } from 'react';

import Language from '../../entity/Language';
import { OrganizationDetailFragment } from '../../generated/graphql';
import { openAsNewTab } from '../../utility/openAsNewTab';
import { context as ConfigContext } from '../context/config';
import { context as DependencyContext } from '../context/dependency';
import { context as HistoryContext } from '../context/history';
import { context as StateContext } from '../context/state';
import { ToolBarForPC } from './ToolBarForPC';
import { ToolBarForSP } from './ToolBarForSP';

type Props = {
  shouldHideSideBar: boolean;
  isOpenedByIframe: boolean;
};

export type SideBarProps = {
  availableOrganizations: Array<OrganizationDetailFragment>;
  selectedOrganization: OrganizationDetailFragment | null;
  signedInUser: DirectorUser;
  onChangeSelectedOrganization: (organization: OrganizationDetailFragment) => void;
  onClickLogo: () => void;
  onClickHomeButton: () => void;
  onClickProjectsButton: () => void;
  onClickNewProjectButton: () => void;
  onClickCreative: () => void;
  onClickSettings: () => void;
  onClickAccountSettings: () => void;
  onClickAnalyticsButton: () => void;
  onClickSignOut: () => void;
  language: Language;
};

const Toolbar: FC<Props> = ({ shouldHideSideBar, isOpenedByIframe }) => {
  const dependency = useContext(DependencyContext);
  const history = useContext(HistoryContext);
  const state = useContext(StateContext);
  const config = useContext(ConfigContext);

  const handleClickNewProjectButton = () => {
    const organization = state.lastAccessedOrganization;
    const user = state.authentication.signedInUser;

    if (organization) {
      const region = organization.businessRegion.key;

      switch (region) {
        case 'jp':
        case 'us':
        case 'gl':
          history.push(`/direction_types`);
          break;
        default:
          break;
      }
    } else if (user) {
      // Organizationが存在しない場合、モーダルを表示してIDへの組織登録を促す
      // Organization.BusinessRegionを参照できないため、Langを参照する
      // https://kaizen.qiita.com/yuta-yamamoto/items/7d1a72e3d01ec3c5ec8f
      const language = state.language;

      switch (language) {
        case 'ja-JP':
        case 'en-US':
          history.push(`/direction_types`);
          break;
        default:
          break;
      }
    }
  };

  const sideBarProps: SideBarProps = {
    availableOrganizations: state.availableOrganizations.items,
    selectedOrganization: state.lastAccessedOrganization,
    signedInUser: state.authentication.signedInUser as DirectorUser,
    onChangeSelectedOrganization: (organization: OrganizationDetailFragment) =>
      history.push(`/latest-insights?org=${organization.id}`),
    onClickLogo: () => history.push('/latest-insights'),
    onClickHomeButton: () => history.push('/latest-insights'),
    onClickProjectsButton: () => history.push(`/projects`),
    onClickNewProjectButton: handleClickNewProjectButton,
    onClickCreative: () => openAsNewTab(`/search?org=${state.lastAccessedOrganization?.id}`),
    onClickSettings: () => history.push('/settings'),
    onClickAccountSettings: () => openAsNewTab(`${config.ID_WEB_URL}/user_setting/general`),
    onClickAnalyticsButton: () => history.push(`/ad-creatives`),
    onClickSignOut: () => dependency.authenticationUsecase.signOut(),
    language: state.language,
  };

  return !state.fatalError && state.authentication.fetching === 0 && state.authentication.signedInUser ? (
    shouldHideSideBar ? (
      <ToolBarForSP sideBarProps={sideBarProps} isOpenedByIframe={isOpenedByIframe} />
    ) : (
      <ToolBarForPC sideBarProps={sideBarProps} isOpenedByIframe={isOpenedByIframe} />
    )
  ) : null;
};

export default Toolbar;
